// docs/52 — hidden things and lair traps as the party sees them: what a trap is called once it is
// known, whether one is still live, and the words the board and the cursor use. Pure functions.
import type { FixtureView, TrapKindName } from './types';
import { shrineLabel, stairLabel } from '../crawl/crawl';
import { curioLine } from '../snares/snares';

/** `fk_sim::TrapKind::name`: what a trap is called once someone has seen it for what it is. */
export const TRAP_NAMES: Record<TrapKindName, string> = {
  Darts: 'a dart trap',
  Pit: 'a covered pit',
  Jaws: 'a jaw trap',
  Rune: 'a fire rune',
  Chute: 'a chute',
};

/** Damage and hold per trap (`TrapKind::dmg` / `root_pulses`), for the inspect line. */
export const TRAP_BITE: Record<TrapKindName, { dmg: number; hold: number }> = {
  Darts: { dmg: 3, hold: 0 },
  Pit: { dmg: 2, hold: 1 },
  Jaws: { dmg: 2, hold: 1 },
  Rune: { dmg: 4, hold: 0 },
  Chute: { dmg: 1, hold: 0 },
};

/**
 * A trap that may still bite: disguised (the party's view never shows one armed, so `known: false`
 * is the only sign it is live) or seen and still armed.
 */
export function trapLive(f: FixtureView): boolean {
  return f.kind === 'Trap' && (!f.known || !!f.armed);
}

/** The name a fixture goes by on the board: a known trap by what it is, anything else by its own. */
export function secretName(f: FixtureView): string {
  if (f.kind === 'Trap' && f.known && f.trap) return TRAP_NAMES[f.trap];
  return f.name || 'something';
}

/** One line for the inspect card and the cursor. */
export function secretLine(f: FixtureView): string {
  switch (f.kind) {
    case 'Trap': {
      if (!f.known) return `${cap(f.name)} — something about it is not right`;
      const bite = f.trap ? TRAP_BITE[f.trap] : undefined;
      if (!f.armed) return `${cap(secretName(f))} — spent or made safe`;
      // docs/60 DC9: a chute drops whoever steps on it into a sealed cell elsewhere on the board
      if (f.trap === 'Chute') return `${cap(secretName(f))} — drops whoever steps on it into a cell below; a rogue can disarm it`;
      return `${cap(secretName(f))} — ${bite ? `${bite.dmg} damage${bite.hold ? `, held ${bite.hold} turn${bite.hold === 1 ? '' : 's'}` : ''}` : 'armed'}; a rogue can disarm it`;
    }
    // a shortcut is walked through to the far side; a hollow is stepped into for what it hides
    case 'Passage':
      if (f.nook) return f.on ? `${cap(f.name)} — open; step in to reach what it hides` : `${cap(f.name)} — found; open it from beside it, then step in`;
      return f.on ? `${cap(f.name)} — open; a shortcut through to the far side — walk through it` : `${cap(f.name)} — found; a shortcut through to the far side — open it from beside it`;
    case 'Clue': return `${cap(f.name)} — it showed where something was hidden`;
    // docs/76 EE3: a hidden door opens from beside it; a stairway down is then taken from beside it
    case 'HiddenDoor':
      if (!f.on) return `${cap(f.name)} — found; open it from beside it`;
      return f.below !== undefined ? `${cap(f.name)} — open; a stair goes down to a room below — touch it from beside it` : `${cap(f.name)} — open`;
    // docs/60: a crawl's stairs and shrines name what they do
    case 'Stair': return `${stairLabel(f)} — to another depth; pick a hero and touch it from beside it`;
    case 'Shrine': return shrineLabel(f);
    // docs/61: a wonder says whether it has been beheld
    case 'Wonder': return f.on ? `${cap(f.name)} — found; every hero may Behold it once for a blessing` : `${cap(f.name)} — a wonder; walk beside it and Behold it`;
    // docs/140 DT3: a dungeon trap's curio says what it is and that it might do something
    case 'Curio': return curioLine(f);
    default: return cap(f.name);
  }
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
